
import React, { useState } from "react"
import { Calendar, Clock, MapPin, MessageSquare, CheckCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "@/hooks/use-toast"
import { useCommonAreas } from "@/hooks/useCommonAreas"
import { useCreateBooking } from "@/hooks/useBookings"

interface AgendamentoModalProps {
  children: React.ReactNode
}

const AgendamentoModal = ({ children }: AgendamentoModalProps) => {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({
    area_id: "",
    booking_date: "",
    start_time: "",
    end_time: "",
    notes: ""
  })

  const { data: areas = [] } = useCommonAreas()
  const createBooking = useCreateBooking()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.area_id || !formData.booking_date || !formData.start_time || !formData.end_time) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios",
        variant: "destructive"
      })
      return
    }

    if (formData.start_time >= formData.end_time) {
      toast({
        title: "Erro",
        description: "A hora de início deve ser anterior à hora de fim",
        variant: "destructive"
      })
      return
    }

    // Verificar se a data e horário não são no passado (considerando horário de Brasília)
    const selectedDate = new Date(formData.booking_date)
    const today = new Date()
    
    // Converter para horário de Brasília (UTC-3)
    const brasiliaOffset = -3 * 60 // -3 horas em minutos
    const now = new Date()
    const brasiliaTime = new Date(now.getTime() + (brasiliaOffset * 60 * 1000))
    
    // Verificar se é data passada
    if (selectedDate < new Date(brasiliaTime.toDateString())) {
      toast({
        title: "Erro",
        description: "Não é possível agendar para datas passadas",
        variant: "destructive"
      })
      return
    }
    
    // Se for o dia atual, verificar se o horário é futuro
    const isToday = selectedDate.toDateString() === new Date(brasiliaTime.toDateString()).toDateString()
    if (isToday) {
      const currentTimeInMinutes = brasiliaTime.getHours() * 60 + brasiliaTime.getMinutes()
      const startTimeInMinutes = parseInt(formData.start_time.split(':')[0]) * 60 + parseInt(formData.start_time.split(':')[1])
      
      if (startTimeInMinutes <= currentTimeInMinutes) {
        toast({
          title: "Erro",
          description: "Para agendamentos no dia atual, o horário deve ser posterior ao horário atual",
          variant: "destructive"
        })
        return
      }
    }

    try {
      await createBooking.mutateAsync(formData)
      setOpen(false)
      setFormData({
        area_id: "",
        booking_date: "",
        start_time: "",
        end_time: "",
        notes: ""
      })
    } catch (error) {
      // Error já tratado no hook
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const availableAreas = areas.filter(area => area.status === 'available')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Novo Agendamento</span>
          </DialogTitle>
          <DialogDescription>
            Solicite o agendamento de uma área comum. Sua solicitação será enviada para aprovação do síndico.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="area">Área Comum *</Label>
            <Select value={formData.area_id} onValueChange={(value) => handleInputChange("area_id", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma área" />
              </SelectTrigger>
              <SelectContent>
                {availableAreas.map((area) => (
                  <SelectItem key={area.id} value={area.id}>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{area.image_emoji}</span>
                      <span>{area.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="booking_date">Data *</Label>
            <Input
              id="booking_date"
              type="date"
              value={formData.booking_date}
              onChange={(e) => handleInputChange("booking_date", e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_time">Hora de Início *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => handleInputChange("start_time", e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time">Hora de Fim *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => handleInputChange("end_time", e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Descreva detalhes sobre o evento ou uso da área..."
                className="pl-10 min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={createBooking.isPending}>
              <CheckCircle className="h-4 w-4 mr-2" />
              {createBooking.isPending ? "Agendando..." : "Solicitar Agendamento"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AgendamentoModal
